import { Spin } from "antd";
import { BarChart3, Target, Trophy } from "lucide-react";
import { useState } from "react";

import type { MemberConsumptionStatsVO } from "@/api/member/type";
import type { MarketingAchievementMemberRowVO } from "@/api/memberBenefit/type";
import { EmptyBlock } from "@/components/DetailBlocks";
import { cn } from "@/lib/cn";

import {
  achievementProgressPercent,
  formatAchievementProgressPair,
  isAchievementUnlocked,
} from "./achievementUtils";
import { useMemberAchievements } from "./useMemberAchievements";

/** 统计子 Tab */
type StatSubTab = "achievement" | "honor" | "data";

/** 数据指标项 */
const DATA_METRICS: {
  key: keyof MemberConsumptionStatsVO;
  label: string;
  unit: string;
  format: (v: number) => string;
}[] = [
  {
    key: "cumulativeRecharge",
    label: "累计充值",
    unit: "元",
    format: (v) =>
      v.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  {
    key: "cumulativeInternetFee",
    label: "累计上网",
    unit: "元",
    format: (v) =>
      v.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  {
    key: "cumulativeShopping",
    label: "累计购物",
    unit: "元",
    format: (v) =>
      v.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  {
    key: "cumulativeBonusGranted",
    label: "累计赠送",
    unit: "元",
    format: (v) =>
      v.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  {
    key: "onlineSessionCount",
    label: "上机次数",
    unit: "次",
    format: (v) => String(Math.round(v)),
  },
  {
    key: "totalOnlineDurationHours",
    label: "累计时长",
    unit: "h",
    format: (v) => v.toFixed(1),
  },
  {
    key: "avgOnlineDurationHours",
    label: "平均时长",
    unit: "h",
    format: (v) => v.toFixed(1),
  },
  {
    key: "cumulativePointsEarned",
    label: "累计积分",
    unit: "分",
    format: (v) => Math.round(v).toLocaleString("zh-CN"),
  },
];

const SUB_TABS: { key: StatSubTab; label: string; icon: typeof Target }[] = [
  { key: "achievement", label: "成就", icon: Target },
  { key: "honor", label: "荣誉", icon: Trophy },
  { key: "data", label: "数据", icon: BarChart3 },
];

export interface StatsTabProps {
  /** 会员 ID */
  memberId: string;
  /** 门店 ID；成就接口必填 */
  shopId?: string | null;
  /** 消费统计；接口未返回时展示空态 */
  stats?: MemberConsumptionStatsVO | null;
}

/** 单条成就行 */
const AchievementRow = ({ row }: { row: MarketingAchievementMemberRowVO }) => {
  const unlocked = isAchievementUnlocked(row);
  const level = row.currentTierLabel?.trim() || "未解锁";
  const { current, target } = formatAchievementProgressPair(row);
  const progress = achievementProgressPercent(row);

  return (
    <div className="flex flex-col gap-1.5 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              "text-sm font-medium truncate",
              unlocked ? "text-slate-800" : "text-slate-400 opacity-50",
            )}
          >
            {row.name || "-"}
          </span>
          <span
            className={cn(
              "text-xs px-1.5 py-0.5 rounded shrink-0",
              unlocked ? "text-slate-600 bg-slate-100" : "text-slate-400 bg-slate-50",
            )}
          >
            {level}
          </span>
        </div>
        <div className="text-sm shrink-0">
          <span className={cn("font-normal", unlocked ? "text-slate-800" : "text-slate-400")}>
            {current}
          </span>
          <span className="text-slate-400"> /{target}</span>
        </div>
      </div>
      {row.howToObtain ? <p className="text-xs text-slate-400 m-0">{row.howToObtain}</p> : null}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-slate-300/60 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

/**
 * 会员详情 - 统计 Tab：成就 / 荣誉 / 数据
 */
export const StatsTab = ({ memberId, shopId, stats }: StatsTabProps) => {
  const [subTab, setSubTab] = useState<StatSubTab>("achievement");
  const { loading, records } = useMemberAchievements({
    memberId,
    shopId,
    enabled: subTab === "achievement",
  });

  return (
    <div>
      <div className="flex items-center gap-1 mb-3 bg-slate-100/80 rounded-lg p-1 w-fit">
        {SUB_TABS.map((sub) => {
          const Icon = sub.icon;
          const active = subTab === sub.key;
          return (
            <button
              key={sub.key}
              type="button"
              onClick={() => setSubTab(sub.key)}
              className={cn(
                "flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                active
                  ? "bg-white text-orange-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700",
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {sub.label}
            </button>
          );
        })}
      </div>

      {subTab === "achievement" && (
        <Spin spinning={loading}>
          {!shopId ? (
            <EmptyBlock text="缺少门店信息，无法加载成就" />
          ) : records.length === 0 && !loading ? (
            <EmptyBlock text="暂无成就数据" />
          ) : (
            <div className="flex flex-col gap-1 divide-y divide-slate-100">
              {records.map((row, index) => (
                <AchievementRow key={String(row.ruleId ?? index)} row={row} />
              ))}
            </div>
          )}
        </Spin>
      )}
      {subTab === "honor" && <EmptyBlock text="暂无荣誉数据" />}
      {subTab === "data" &&
        (stats ? (
          <div className="grid grid-cols-2 gap-2">
            {DATA_METRICS.map((metric) => {
              const raw = stats[metric.key];
              const num = typeof raw === "number" ? raw : Number(raw ?? 0);
              return (
                <div
                  key={metric.key}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                >
                  <span className="text-sm text-slate-500">{metric.label}</span>
                  <span className="text-sm font-medium text-slate-800">
                    {metric.format(Number.isFinite(num) ? num : 0)}
                    <span className="text-xs font-normal ml-0.5 text-slate-500">{metric.unit}</span>
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyBlock text="暂无统计数据" />
        ))}
    </div>
  );
};
